<template>
    <div class="rows-min-minmax">
        <div class="_p-1 _mb-1 f-x-between">
            <div class="f space-x-1">
                <!-- Task view mode -->
                <el-radio-group v-model="viewMode"
                                size="mini"
                                @change="changeTasksView">
                    <el-radio-button label="list">
                        {{ labels.viewMode.list }}
                    </el-radio-button>
                    <el-radio-button label="calendar">
                        {{ labels.viewMode.calendar }}
                    </el-radio-button>
                </el-radio-group>
                
                <our-label v-if="isViewModeCalendar"
                           :value="currentDateLabel"
                           primary />
            </div>
    
            <!-- Task tools -->
            <div v-show="isViewModeList"
                 ref="taskTools"
                 class="f space-x-0.5">
                <!-- Task type -->
                <el-radio-group v-model="taskType"
                                size="mini"
                                @change="applyTaskType">
                    <el-radio-button :label="null">
                        {{ labels.all }}
                    </el-radio-button>
                    <el-radio-button v-for="item in taskTypes"
                                     :key="item.type"
                                     :label="item.type">
                        {{ item.label }}
                    </el-radio-button>
                </el-radio-group>
    
    
                <!-- Task filter -->
                <task-filter ref="taskFilter"
                             v-model="taskFilter"
                             :type="taskType"
                             @confirm="confirmFilter" />
            </div>
    
            <div v-show="isViewModeCalendar"
                 class="f-y-center space-x-1">
                <div class="space-x-0.5">
                    <!-- Prev -->
                    <el-button plain
                               icon="el-icon-arrow-left"
                               size="mini"
                               @click="prev" />
    
                    <!-- Date -->
                    <el-date-picker v-model="currentDate"
                                    :type="calendarViewMonth ? 'month' : 'week'"
                                    :format="calendarViewMonth ? 'MMMM, yyyy' : 'W Неделя'"
                                    :clearable="false"
                                    :placeholder="`Выбрать ${calendarViewMonth ? 'месяц' : 'неделю'}`"
                                    :picker-options="{
                                        firstDayOfWeek: 1
                                    }"
                                    size="mini"
                                    @change="changeDate" />
    
                    <!-- Next -->
                    <el-button plain
                               icon="el-icon-arrow-right"
                               size="mini"
                               @click="next" />
                </div>
    
                <el-button type="text"
                           icon="el-icon-date"
                           size="mini"
                           @click="handleChangeCalendarView">
                    {{ labels.calendarViews[calendarView] }}
                </el-button>
            </div>
        </div>
        
        <div ref="tasks">
            <keep-alive>
                <!-- Table -->
                <task-table v-if="isViewModeList"
                            ref="table"
                            v-loading="tasksLoading"
                            class="h-full"
                            :tasks="tasks"
                            :pagination="tasksPagination"
                            @paginate="paginate"
                            @resize="resize"
                            @task-click="setViewedTask"
                            @sort="sort" />
                            
                <!-- Calendar -->
                <task-calendar v-else
                               ref="calendar"
                               :current-date="currentDate"
                               :calendar-view="calendarView"
                               @task-click="setViewedTask" />
            </keep-alive>
        </div>
        
        <!-- Viewed task -->
        <task-viewer 
            :task="viewedTask"
            @close="clearViewedTask" />
    </div>
</template>

<script>
import { addMonths, addWeeks, endOfWeek, format, startOfWeek, subMonths, subWeeks } from 'date-fns'
import locale from 'date-fns/locale/ru'

import {  pagenable } from '@/store/connectors'

import { types, translateType } from '@/models/tasks'

import TaskTable from '@/components/tasks/TaskTable'
import TaskCalendar from '@/components/tasks/TaskCalendar'
import TaskViewer from '@/components/tasks/TaskViewer'
import TaskFilter from '@/components/tasks/TaskFilter'

const taskTypes = [
  types.DEFECTS_AND_VIOLATIONS,
  //types.BUILDING_ORDER,
  types.ACCEPTANCE_OF_WORK,
  types.PHOTO_ORDER,
  types.APPROVAL_PROCESS
].map(x => ({
  type: x,
  label: translateType(x, { capital: true })
}))

const labels = {
  tasks: 'Мои задачи',
  calendar: 'Мой календарь',
  all: 'Все',
  taskTools: 'Фильтр задач',

  viewMode: {
    list: 'Список',
    calendar: 'Календарь'
  },

  calendarViews: {
    dayGridMonth: 'Месяц',
    dayGridWeek: 'Неделя'
  }
}

export default {
  name: 'MyTasks',
  components: {
    TaskTable,
    TaskCalendar,
    TaskViewer,
    TaskFilter
  },
  mixins: [
    pagenable({ on: 'account', name: 'tasks' })
  ],
  data() {
    return {
      viewMode: null,

      taskSort: null,
      taskType: null,
      taskFilter: {},
      taskFilterForServer: {},

      viewedTask: null,

      currentDate: new Date(),
      calendarView: 'dayGridWeek',
      
      labels,
      taskTypes
    }
  },
  computed: {
    isViewModeList() {
      return this.viewMode === 'list'
    },

    isViewModeCalendar() {
      return this.viewMode === 'calendar'
    },

    calendarViewMonth() {
      return this.calendarView === 'dayGridMonth'
    },

    currentDateLabel() {
      if (this.calendarViewMonth) {
        const date = this.currentDate && format(this.currentDate, 'LLLL yyyy', { locale })
        return date && date.charAt(0).toUpperCase() + date.slice(1)
      } else {
        const start = format(startOfWeek(this.currentDate, { locale }), 'dd MMMM', { locale })
        const end = format(endOfWeek(this.currentDate, { locale }), 'dd MMMM yyyy', { locale })
        return `${start} - ${end}`
      }
    }
  },
  mounted() {
    const { view } = this.$route.query
    
    if (view) {
      this.viewMode = view
    } else {
      this.changeTasksView('list')
    }

    this.fetch()
  },
  methods: {
    fetch() {
      this.fetchTasks({ 
        filter: {
          type: this.taskType,
          ...this.taskFilterForServer
        },
        sort: this.taskSort || ({
          [types.DEFECTS_AND_VIOLATIONS]: ['first_draft', 'kind_defect', '-created_at']
        })[this.taskType] || ['-created_at'],
        withMembers: true,
        withKs2: true,
        withKs2ErrorsExist: true,
        withTaskRejectionsExistAsTaskReasonDefect: true,
        withTaskRejectionsExistThroughAstWorks: true
      })
    },
    
    confirmFilter([filter, filterForServer]) {
      this.taskFilter = filter
      this.taskFilterForServer = filterForServer
      this.fetch()
    },

    paginate(page) {  
      this.paginateTasks({ page })
      this.fetch()
    },

    resize(size) {
      this.paginateTasks({ size })
      this.fetch()
    },

    sort({ key, order }) {
      this.taskSort = this.generateSortParams(key, order)
      this.fetch()
    },
    
    applyTaskType() {
      this.taskFilter = {}
      this.taskFilterForServer = {}
      this.fetch()
    },

    async setViewedTask(task) {
      this.viewedTask = task
    },

    clearViewedTask() {
      this.viewedTask = null
    },

    changeDate(date) {
      this.currentDate = date
      this.$refs.calendar.$refs.taskCalendar.getApi().gotoDate(date)
    },

    prev() {
      this.currentDate = this.calendarViewMonth ? subMonths(this.currentDate, 1) : subWeeks(this.currentDate, 1)
      this.$refs.calendar.$refs.taskCalendar.getApi().prev()
    },

    next() {
      this.currentDate = this.calendarViewMonth ? addMonths(this.currentDate, 1) : addWeeks(this.currentDate, 1)
      this.$refs.calendar.$refs.taskCalendar.getApi().next()
    },

    changeTasksView(x) {
      this.viewMode = x
      this.$router.push({
        name: 'project.home.my-tasks',
        query: { view: x }
      })
    },

    handleChangeCalendarView() {
      this.calendarView = this.calendarView === 'dayGridMonth' ? 'dayGridWeek' : 'dayGridMonth'
    }
  }
}
</script>